import React, { useState, useRef } from 'react';
import { X, UploadSimple, FileText, WarningCircle, ArrowRight, ArrowLeft, CircleNotch, FolderPlus, Sparkle } from '@phosphor-icons/react';
import { projectService } from '../../../services/project.service';
import type { ProjectAIMetadata, ProjectProposedFields } from '../../../services/project.service';
import { useCategories } from '../hooks/useCategories';
import { toast } from 'sonner';
import posthog from '../../../posthog';

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateProjectModal({ isOpen, onClose, onSuccess }: CreateProjectModalProps) {
  const { categoryMap } = useCategories();

  const [step, setStep] = useState<1 | 2>(1);

  // Step 1 State
  const [causeId, setCauseId] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step 2 State
  const [causeName, setCauseName] = useState('');
  const [category, setCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [summary, setSummary] = useState('');
  const [objective, setObjective] = useState('');
  const [remarks, setRemarks] = useState('');

  // Set when the AI's category/sub_category answer isn't in categoryMap, so
  // it was left for the user to pick instead of being assigned unvalidated.
  const [offListWarning, setOffListWarning] = useState<string | null>(null);

  const [aiProposed, setAiProposed] = useState<ProjectProposedFields | null>(null);
  const [aiMetadata, setAiMetadata] = useState<ProjectAIMetadata | null>(null);
  const [documentText, setDocumentText] = useState<string | null>(null);
  const [nameSource, setNameSource] = useState<'records' | 'ai' | 'manual' | null>(null);

  const [creating, setCreating] = useState(false);

  if (!isOpen) return null;

  const handleFileSelect = (newFiles: FileList | null) => {
    if (!newFiles) return;
    const fileArray = Array.from(newFiles);
    setFiles((prev) => [...prev, ...fileArray]);
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleNextStep = async () => {
    if (!causeId.trim()) {
      toast.error('Please enter a Project ID / Cause ID');
      return;
    }

    setIsAnalyzing(true);
    setOffListWarning(null);

    // 1. Always check payment records / existing projects first for the
    //    canonical cause name. monday_data is the authoritative source —
    //    the AI's extracted project_name often differs from the official
    //    name stored in payment records.
    let recordName = '';
    try {
      const suggestion = await projectService.suggestCause({ cause_id: causeId.trim() });
      if (suggestion.cause_name) {
        recordName = suggestion.cause_name;
        setCauseName(recordName);
        setNameSource('records');
      }
    } catch (e) {
      // Ignore suggestion errors
    }

    // 2. If files uploaded, run AI analysis for classification fields
    if (files.length > 0) {
      try {
        const { ai_proposed, ai_metadata, document_text } = await projectService.extractMetadataFromDocuments(files);
        setAiProposed(ai_proposed);
        setAiMetadata(ai_metadata);
        setDocumentText(document_text || null);

        // Only use AI's project_name if payment records didn't provide one
        if (!recordName && ai_proposed.project_name) {
          setCauseName(ai_proposed.project_name);
          setNameSource('ai');
        }

        const aiCategory = ai_proposed.category || '';
        const aiSubCategory = ai_proposed.sub_category || '';

        if (aiCategory && categoryMap[aiCategory]) {
          setCategory(aiCategory);
          if (aiSubCategory && categoryMap[aiCategory].includes(aiSubCategory)) {
            setSubCategory(aiSubCategory);
          } else {
            setSubCategory('');
            if (aiSubCategory) {
              setOffListWarning(
                `The AI answered "${aiSubCategory}", which is not a valid sub-category for ${aiCategory}. Please choose one.`
              );
            }
          }
        } else {
          setCategory('');
          setSubCategory('');
          if (aiCategory) {
            setOffListWarning(`The AI answered "${aiCategory}", which is not a valid category. Please choose one.`);
          }
        }

        setSummary(ai_proposed.summary || '');
        setObjective(ai_proposed.objective || '');
        setRemarks(ai_proposed.remarks || '');
        toast.success('Extracted details from document');
      } catch (err) {
        console.error('Failed to extract metadata:', err);
        toast.error('Failed to analyze document. You can still enter details manually.');
      }
    }

    setIsAnalyzing(false);
    setStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!causeId.trim()) {
      toast.error('Project ID is required');
      return;
    }
    if (!causeName.trim()) {
      toast.error('Project Name is required');
      return;
    }

    setCreating(true);

    try {
      await projectService.createProject({
        cause_id: causeId.trim(),
        cause_name: causeName.trim(),
        category: category.trim(),
        sub_category: subCategory.trim(),
        summary: summary.trim(),
        objective: objective.trim(),
        remarks: remarks.trim(),
        ai_metadata: aiMetadata || undefined,
        ai_proposed: aiProposed || undefined,
        document_text: documentText || undefined,
      });

      // Upload any supporting documents to S3 now that the project record exists.
      if (files.length > 0) {
        try {
          await projectService.uploadProjectDocuments(causeId.trim(), files);
        } catch (uploadErr) {
          console.warn('S3 upload failed (project still created):', uploadErr);
          toast.warning('Project created, but document upload to storage failed.');
        }
      }

      posthog.capture('project_created', { has_supporting_documents: files.length > 0 });
      toast.success('Project created successfully!');
      onSuccess();
      handleClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to create project. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setCauseId('');
    setCauseName('');
    setCategory('');
    setSubCategory('');
    setSummary('');
    setObjective('');
    setRemarks('');
    setFiles([]);
    setOffListWarning(null);
    setAiProposed(null);
    setAiMetadata(null);
    setDocumentText(null);
    setNameSource(null);
    onClose();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xs animate-in fade-in duration-200">
      <div
        className="bg-white border border-slate-200/80 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-accent/10 flex items-center justify-center text-accent">
              <FolderPlus className="w-4 h-4" weight="fill" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-sm">
                {step === 1 ? 'Create New Project' : 'Project Details'}
              </h3>
              <p className="text-[11px] text-slate-500 font-medium">
                {step === 1 ? 'Enter ID & upload supporting documents' : 'Review and edit project details'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={creating || isAnalyzing}
            className="text-slate-400 hover:text-slate-600 transition-colors p-1.5 rounded-full hover:bg-slate-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form Body */}
        {step === 1 ? (
          <div className="p-6 space-y-4 overflow-y-auto flex-1">
            <div className="space-y-1">
              <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                Project ID / Cause ID <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                required
                placeholder="e.g. PRJ-2026-001 or UNIQUE_CAUSE_ID"
                value={causeId}
                onChange={(e) => setCauseId(e.target.value)}
                disabled={isAnalyzing}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-medium text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-all"
              />
            </div>

            {/* Document Upload Zone */}
            <div className="space-y-1.5 pt-2">
              <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider flex items-center justify-between">
                <span>Project Documents (Optional)</span>
              </label>

              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-2xl p-4 text-center cursor-pointer transition-all ${
                  isDragging
                    ? 'border-accent bg-accent/5'
                    : 'border-slate-200 hover:border-slate-300 bg-slate-50/50 hover:bg-slate-50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.docx,.pptx,.txt,image/*"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files)}
                  disabled={isAnalyzing}
                />
                <div className="flex flex-col items-center gap-1">
                  <UploadSimple className="w-5 h-5 text-accent" />
                  <p className="text-xs font-semibold text-slate-700">Click to upload or drag files here</p>
                  <p className="text-[9px] text-slate-400">PDF, DOCX, PPTX, TXT, images</p>
                </div>
              </div>

              {/* Selected files list */}
              {files.length > 0 && (
                <div className="space-y-1.5 max-h-32 overflow-y-auto pt-2">
                  {files.map((file, idx) => (
                    <div
                      key={`${file.name}-${idx}`}
                      className="flex items-center justify-between bg-slate-50 border border-slate-200/60 px-3 py-1.5 rounded-xl text-xs"
                    >
                      <div className="flex items-center gap-2 min-w-0 pr-2">
                        <FileText className="w-4 h-4 text-accent shrink-0" />
                        <span className="font-medium text-slate-700 truncate">{file.name}</span>
                        <span className="text-[10px] text-slate-400 shrink-0">({formatFileSize(file.size)})</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(idx)}
                        disabled={isAnalyzing}
                        className="text-slate-400 hover:text-rose-500 transition-colors p-1"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <form id="project-form" onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                  Project Name <span className="text-rose-500">*</span>
                </label>
                {nameSource === 'records' && (
                  <span className="text-[9px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                    ✓ From payment records
                  </span>
                )}
                {nameSource === 'ai' && (
                  <span className="text-[9px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                    ⚠ AI suggestion — verify against records
                  </span>
                )}
              </div>
              <input
                type="text"
                required
                value={causeName}
                onChange={(e) => {
                  setCauseName(e.target.value);
                  if (nameSource !== 'manual') setNameSource('manual');
                }}
                disabled={creating}
                placeholder="Enter or paste the official project name from records..."
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-medium text-slate-800 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-all"
              />
              {!causeName && !nameSource && (
                <p className="text-[10px] text-slate-400 font-medium leading-snug">
                  No matching payment record found for this Cause ID. Please enter the project name manually or paste it from your records.
                </p>
              )}
            </div>

            {offListWarning && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                <WarningCircle size={14} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-[10px] font-semibold text-amber-700 leading-snug">{offListWarning}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                  Category
                </label>
                <select
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    setSubCategory('');
                    setOffListWarning(null);
                  }}
                  disabled={creating}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-medium text-slate-800 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-all"
                >
                  <option value="">Select Category</option>
                  {Object.keys(categoryMap).map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                  Sub-Category
                </label>
                <select
                  value={subCategory}
                  onChange={(e) => {
                    setSubCategory(e.target.value);
                    setOffListWarning(null);
                  }}
                  disabled={creating || !category}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-medium text-slate-800 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-all"
                >
                  <option value="">Select Sub-Category</option>
                  {category && categoryMap[category] && categoryMap[category].map((subName) => (
                    <option key={subName} value={subName}>
                      {subName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-1">
              <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                Summary
              </label>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                disabled={creating}
                rows={3}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-medium text-slate-800 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-all resize-none"
              />
            </div>

            <div className="space-y-1">
              <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                Objective
              </label>
              <textarea
                value={objective}
                onChange={(e) => setObjective(e.target.value)}
                disabled={creating}
                rows={3}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-medium text-slate-800 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-all resize-none"
              />
            </div>

            <div className="space-y-1">
              <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                Remarks
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                disabled={creating}
                rows={2}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs font-medium text-slate-800 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-all resize-none"
              />
            </div>
          </form>
        )}

        {/* AI Correction Indicator */}
        {step === 2 && aiProposed && (
          category !== (aiProposed.category || '') ||
          subCategory !== (aiProposed.sub_category || '') ||
          summary !== (aiProposed.summary || '') ||
          objective !== (aiProposed.objective || '') ||
          remarks !== (aiProposed.remarks || '')
        ) && (
          <div className="px-6 pb-4 shrink-0 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="flex items-start gap-2.5 bg-accent/5 border border-accent/20 rounded-xl p-3">
              <Sparkle className="w-4 h-4 text-accent shrink-0 mt-0.5" weight="fill" />
              <div>
                <p className="text-[11px] font-bold text-accent leading-tight">Correct AI prompt</p>
                <p className="text-[10px] text-slate-600 mt-0.5 leading-snug">
                  You've modified fields that were pre-filled by the AI. When you create this project, the system will try  learn from your corrections to improve future analysis.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50 shrink-0">
          {step === 1 ? (
            <>
              <button
                type="button"
                onClick={handleClose}
                disabled={isAnalyzing}
                className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 font-semibold text-xs hover:bg-slate-50 transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleNextStep}
                disabled={isAnalyzing}
                className="flex items-center gap-2 px-5 py-2 rounded-xl bg-accent text-white font-bold text-xs shadow-md shadow-accent/20 hover:bg-accent/90 disabled:opacity-50 transition-all"
              >
                {isAnalyzing ? (
                  <>
                    <CircleNotch className="w-4 h-4 animate-spin" />
                    {files.length > 0 ? 'Analyzing...' : 'Loading...'}
                  </>
                ) : (
                  <>
                    {files.length > 0 ? 'Next (Analyze Files)' : 'Skip (Manual Entry)'}
                    <ArrowRight className="w-4 h-4" weight="bold" />
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setStep(1)}
                disabled={creating}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-slate-600 font-semibold text-xs hover:bg-slate-50 transition-all"
              >
                <ArrowLeft className="w-4 h-4" weight="bold" />
                Back
              </button>
              <button
                type="submit"
                form="project-form"
                disabled={creating}
                className="flex items-center gap-2 px-5 py-2 rounded-xl bg-accent text-white font-bold text-xs shadow-md shadow-accent/20 hover:bg-accent/90 disabled:opacity-50 transition-all"
              >
                {creating ? (
                  <>
                    <CircleNotch className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FolderPlus className="w-4 h-4" weight="fill" />
                    Create Project
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
